import './PhoneSettingsModal.scss'
import { Modal } from '../../../../components/Modal/Modal'
import { Alert, Col, Container, Row, Spinner, Stack, Tab } from 'react-bootstrap'
import React, { useState } from 'react'
import { Tabs } from '../../../../components/Tabs/Tabs'
import { ExternalUserServiceIntegration } from '../../../../models/User'
import { PhoneNumber } from '../../../../components/PhoneNumber/PhoneNumber'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { Button } from '../../../../components/Button/Button'
import { Divider } from '../../../../components/Divider/Divider'
import { useGetAllServiceLocationsQuery } from '../../../../store/user/user'
import { ContactCardSelector } from '../../../../components/ContactCardSelector/ContactCardSelector'
import { IntegrationServiceId } from '../../../../common/integrations'
import { ClaimPhoneNumberPage } from '../../../Integration/Connectors/ModalIntegrationConnectors/Phone/models'
import { DeletePhoneIntegrationModal } from '../../../Integration/Connectors/ModalIntegrationConnectors/Phone/DeletePhoneIntegrationModal'

export type PhoneSettingsModalProps = {
  show: boolean
  onHide: () => void
  accountId: string
  userId: string
  phoneIntegrations?: ExternalUserServiceIntegration[]
  showClaimPhoneNumberModal: (page?: ClaimPhoneNumberPage, phoneNumber?: string) => void
}
export const PhoneSettingsModal: React.FC<PhoneSettingsModalProps> = ({ show, onHide, accountId, userId, phoneIntegrations, showClaimPhoneNumberModal }) => {
  const [activeTabKey, setActiveTabKey] = useState<string>('phoneNumbers')
  const changeTab = (key: string | null) => {
    if (key) {
      setActiveTabKey(key)
    }
  }

  const PhoneSettingsModalBody = () => {
    const { data: locations, isFetching: locationsFetching } = useGetAllServiceLocationsQuery({
      accountId: accountId,
      serviceId: IntegrationServiceId.PHONE,
    })

    return (
      <Container className='p-0'>
        <Row className='pb-5'>
          <Col>
            <h4 className='d-flex justify-content-center align-items-center'>Edit Phone Integration Settings</h4>
          </Col>
        </Row>
        <Row>
          <Col>
            <Tabs activeKey={activeTabKey} id='phone-settings' className='d-fex mb-3' onSelect={changeTab} mountOnEnter unmountOnExit>
              <Tab eventKey='phoneNumbers' title='Phone Numbers'>
                <PhoneNumbers
                  accountId={accountId}
                  userId={userId}
                  phoneIntegrations={phoneIntegrations}
                  showClaimPhoneNumberModal={showClaimPhoneNumberModal}
                />
              </Tab>
              <Tab eventKey='zocksContactCard' title='Zocks Contact Card'>
                {locationsFetching ? (
                  <div className='d-flex justify-content-center'>
                    <Spinner />
                  </div>
                ) : locations?.data.length ? (
                  <ContactCardSelector locations={locations.data} />
                ) : (
                  <Alert variant='danger'>
                    <Alert.Heading>Oh snap! You got an error!</Alert.Heading>
                    <p>Unable to get any locations. Please contact us.</p>
                  </Alert>
                )}
              </Tab>
              <Tab eventKey='howItWorks' title='How it Works'>
                <PhoneTutorial />
              </Tab>
            </Tabs>
          </Col>
        </Row>
      </Container>
    )
  }

  return <Modal show={show} onHide={onHide} header={<></>} body={<PhoneSettingsModalBody />} footer={<></>} centered={true} className='phone-settings-modal' />
}

type PhoneNumbersProps = {
  accountId: string
  userId: string
  phoneIntegrations?: ExternalUserServiceIntegration[]
  showClaimPhoneNumberModal: (page?: ClaimPhoneNumberPage, phoneNumber?: string) => void
}

const PhoneNumbers: React.FC<PhoneNumbersProps> = ({ accountId, userId, phoneIntegrations, showClaimPhoneNumberModal }) => {
  const [selectedUserIntegration, setSelectedUserIntegration] = useState<ExternalUserServiceIntegration | null>(null)
  const [showDeletePhoneIntegrationModal, setShowDeletePhoneIntegrationModal] = useState<boolean>(false)

  const handleResendCode = (phoneNumber: string) => {
    showClaimPhoneNumberModal(ClaimPhoneNumberPage.PHONE_NUMBER, phoneNumber)
  }

  const handleVerifyCode = (phoneNumber: string) => {
    showClaimPhoneNumberModal(ClaimPhoneNumberPage.VERIFICATION, phoneNumber)
  }

  const handleDeleteUserIntegration = (integration: ExternalUserServiceIntegration): void => {
    setSelectedUserIntegration(integration)
    setShowDeletePhoneIntegrationModal(true)
  }

  const handleDeletePhoneIntegrationModalHide = () => {
    setShowDeletePhoneIntegrationModal(false)
    setSelectedUserIntegration(null)
  }

  type PhoneItemProps = {
    integration: ExternalUserServiceIntegration
  }
  const PhoneNumberStatus: React.FC<PhoneItemProps> = ({ integration }) => {
    switch (integration.state) {
      case 'live':
        return (
          <div className='pt-2 d-flex justify-content-start zocks-text-success'>
            <Stack direction='horizontal' gap={2}>
              <i className='bi bi-check' />
              Validated number
            </Stack>
          </div>
        )
      case 'validating':
        return (
          <div className='pt-2 d-flex justify-content-start zocks-text-warning-text'>
            <Stack direction='horizontal' gap={2}>
              <i className='bi bi-exclamation-circle' />
              Invalid number. Please finish the validation.
            </Stack>
          </div>
        )
      case 'invalid':
        return (
          <div className='pt-2 d-flex justify-content-start zocks-text-warning-text'>
            <Stack direction='horizontal' gap={2}>
              <i className='bi bi-exclamation-circle' />
              {integration.metadata?.invalidity_reason === 'newer_integration' ? 'Used by another user.' : ''} Please validate the number if you want to use it.
            </Stack>
          </div>
        )
      default:
        return <></>
    }
  }

  const PhoneNumberButtons: React.FC<PhoneItemProps> = ({ integration }) => {
    return (
      <ScopedBlock
        scope={{
          scope: 'integration:connect',
          superScope: ['integration:admin'],
        }}
        missingScopeAction={MissingScopeAction.DISABLE}
      >
        <Stack direction='horizontal' gap={2}>
          {integration.state === 'invalid' && (
            <Button variant='primary' onClick={() => handleResendCode(integration.externalId)}>
              Resend code
            </Button>
          )}
          {integration.state === 'validating' && (
            <Button variant='primary' onClick={() => handleVerifyCode(integration.externalId)}>
              Verify code
            </Button>
          )}
          <Button variant='primaryGhost' className='zocks-p-0.75' onClick={() => handleDeleteUserIntegration(integration)}>
            <i className='bi bi-trash' />
          </Button>
        </Stack>
      </ScopedBlock>
    )
  }

  return (
    <Container key='phone-numbers-container' className='p-0'>
      {selectedUserIntegration ? (
        <DeletePhoneIntegrationModal
          accountId={accountId}
          userId={userId}
          integration={selectedUserIntegration}
          show={showDeletePhoneIntegrationModal}
          onHide={handleDeletePhoneIntegrationModalHide}
        />
      ) : null}
      {phoneIntegrations?.length ? (
        phoneIntegrations.map((i, idx) => (
          <div key={i.id}>
            <Row className='d-flex justify-content-between py-md-3'>
              <Col className='px-md-3'>
                <Stack>
                  <h6>
                    <PhoneNumber phoneNumber={i.externalId} />
                  </h6>
                  <PhoneNumberStatus integration={i} />
                </Stack>
              </Col>
              <Col md='auto' className='d-flex p-0'>
                <PhoneNumberButtons integration={i} />
              </Col>
            </Row>
            {idx + 1 < phoneIntegrations.length ? <Divider key={`${i.id}-divider`} className='d-none d-md-block' /> : ''}
          </div>
        ))
      ) : (
        <div>No connected phone numbers.</div>
      )}
    </Container>
  )
}

const PhoneTutorial: React.FC = () => {
  return (
    <Container className='phone-tutorial'>
      <Row className='py-2'>
        <Col>
          <Stack direction='horizontal' gap={3}>
            <h6>1</h6>
            Validate your phone number
          </Stack>
        </Col>
      </Row>
      <Row className='py-2'>
        <Col>
          <Stack direction='horizontal' gap={3}>
            <h6>2</h6>
            Save the contact info of your Zocks AI Assistant to your contacts on your phone.
          </Stack>
        </Col>
      </Row>
      <Row className='py-2'>
        <Col>
          <Stack direction='horizontal' gap={3}>
            <h6>3</h6>
            After starting a phone call, use the 'conference call' option to allow your Zocks Assistant to join as an additional participant.
          </Stack>
        </Col>
      </Row>
      <Row className='py-2'>
        <Col>
          <Stack direction='horizontal' gap={3}>
            <h6>4</h6>
            Once your call is over, take a look at your Meeting Summary to gain important insights.
          </Stack>
        </Col>
      </Row>
      <Row className='pt-5'>
        <Col className='px-0'>
          <div className='paragraph-medium'>
            If you would like to learn more, you can read our full{' '}
            <a
              target='_blank'
              href='https://help.zocks.io/en/articles/9969795-how-to-connect-zocks-to-your-phone-calls'
              rel='noreferrer'
              className='button-link-medium zocks-text-gray-900'
            >
              Help&nbsp;article.
            </a>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
